import { GridPaginationModel } from '@mui/x-data-grid';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApiError } from 'src/api/axiosClient/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useGroupService } from 'src/modules/groups/services';
import { RootState } from 'src/store';
import { debounce } from 'ts-debounce';
import { useContactsWithGroupsService } from '../services';

export interface AddContactsToGroupDialogController {
  groupId: string | null;
  groupName: string | null;
  isOpen: boolean;
  loading: {
    get: boolean;
    add: boolean;
  };
  filter: RootState['contacts']['filterWithGroups'];
  list: RootState['contacts']['listWithGroups'];
  error: ApiError | null;
  open: (groupId: string, groupName?: string) => Promise<boolean>;
  close: () => void;
  changeSearch: (query: string) => Promise<void>;
  changePagination: (p: GridPaginationModel) => Promise<void>;
  // Resolves to whether the contacts were added.
  submit: (contactIds: string[], nextAction?: () => Promise<unknown>) => Promise<boolean>;
}

export const useAddContactsToGroupDialogController = (): AddContactsToGroupDialogController => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null);
  const [currentGroupName, setCurrentGroupName] = useState<string | null>(null);

  const {
    loading: contactsLoading,
    filter,
    list,
    error,
    changeContactsFilter,
    getContacts,
    resetContacts,
  } = useContactsWithGroupsService();
  const { loading: groupLoading, addContactsToGroup } = useGroupService();

  const open = useCallback(
    async (groupId: string, groupName?: string) => {
      setCurrentGroupId(groupId);
      setCurrentGroupName(groupName || null);

      try {
        changeContactsFilter({ isSpam: false });
        await getContacts(currentCompanyId);
        return true;
      } catch (e) {
        console.log(e);
        setCurrentGroupId(null);
        setCurrentGroupName(null);
        resetContacts();
        return false;
      }
    },
    [changeContactsFilter, getContacts, currentCompanyId, resetContacts],
  );

  const close = useCallback(() => {
    setCurrentGroupId(null);
    setCurrentGroupName(null);
    resetContacts();
  }, [resetContacts]);

  const reloadContacts = useCallback(async () => {
    try {
      await getContacts(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadContactsDebounce = useMemo(() => debounce(reloadContacts, 500), []);

  const changeSearch = useCallback(async (query: string) => {
    changeContactsFilter({ query });
    await reloadContactsDebounce();
  }, []);

  const changePagination = useCallback(async (p: GridPaginationModel) => {
    changeContactsFilter({ page: p.page, limit: p.pageSize, offset: p.page * p.pageSize });
    await reloadContacts();
  }, []);

  const submit = useCallback(
    async (contactIds: string[], nextAction?: () => Promise<unknown>) => {
      if (currentGroupId) {
        try {
          await addContactsToGroup(currentCompanyId, currentGroupId, contactIds);
          close();
          await nextAction?.();
          return true;
        } catch (e) {
          console.log(e);
        }
      }
      return false;
    },
    [currentGroupId],
  );

  return {
    groupId: currentGroupId,
    groupName: currentGroupName,
    isOpen: !!currentGroupId,
    loading: {
      get: contactsLoading.getListWithGroups,
      add: groupLoading.addContactsToGroup,
    },
    filter,
    list,
    error,
    open,
    close,
    changeSearch,
    changePagination,
    submit,
  };
};
